import { Cloud, CloudOff, FileText, Film, Music, File as FileIcon, ImageOff } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useMediaObjectUrl } from '@/hooks/useMediaObjectUrl';
import { formatDuration, isUploaded, publicUrl, type MediaItem } from '@/lib/media';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import { cn } from '@/lib/utils';

const KIND_ICON = {
  image: ImageOff,
  video: Film,
  audio: Music,
  pdf: FileText,
  other: FileIcon,
} as const;

interface MediaThumbnailProps {
  item: MediaItem;
  className?: string;
}

/**
 * The square preview used in the media grid and picker.
 *
 * Prefers the locally-generated thumbnail, falls back to the remote URL
 * for items whose bytes have been evicted, and finally to a kind icon
 * for formats with nothing to rasterize (audio, PDF). That ordering
 * matters: the local thumbnail is instant and works offline, whereas the
 * remote URL costs a network round trip.
 */
export function MediaThumbnail({ item, className }: MediaThumbnailProps) {
  const { url, isLoading, missing } = useMediaObjectUrl(item.sha256, 'thumb');
  const remote = sanitizeUrl(publicUrl(item));
  const Icon = KIND_ICON[item.kind];

  // Only images can be shown from a remote URL — a video/PDF URL would
  // not render in an <img>.
  const src = url ?? (missing && item.kind === 'image' ? remote : undefined);

  return (
    <div className={cn('relative aspect-square overflow-hidden rounded-lg bg-muted', className)}>
      {isLoading ? (
        <Skeleton className="size-full" />
      ) : src ? (
        <img src={src} alt="" className="size-full object-cover" loading="lazy" />
      ) : (
        <div className="flex size-full items-center justify-center">
          <Icon className="size-8 text-muted-foreground/50" />
        </div>
      )}

      {/* Upload state is the single most important thing to convey here:
          a local-only file can't be referenced from a published article
          until it's uploaded. */}
      <div className="absolute left-1.5 top-1.5">
        <Badge
          variant={isUploaded(item) ? 'secondary' : 'outline'}
          className="gap-1 bg-background/90 px-1.5 py-0 text-[10px] backdrop-blur"
          title={
            isUploaded(item)
              ? `On ${item.uploads.length} Blossom server${item.uploads.length === 1 ? '' : 's'}`
              : 'Local only — not uploaded yet'
          }
        >
          {isUploaded(item) ? <Cloud className="size-2.5" /> : <CloudOff className="size-2.5" />}
          {isUploaded(item) ? item.uploads.length : 'local'}
        </Badge>
      </div>

      {item.kind !== 'image' && (
        <div className="absolute right-1.5 top-1.5">
          <Badge variant="secondary" className="gap-1 bg-background/90 px-1.5 py-0 text-[10px] backdrop-blur">
            <Icon className="size-2.5" />
            {item.duration ? formatDuration(item.duration) : item.kind}
          </Badge>
        </div>
      )}
    </div>
  );
}
